import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { domainsApi } from '@/features/domains/api';
import { apiErrorMessage } from '@/api/errors';
import { summarizeDnsVerification } from '@/features/domains/verificationSummary';
import type { DomainListParams, ZoneRecordPayload } from '@/features/domains/types';
import type { DomainFormValues } from '@/features/domains/schema';

const keys = {
  all: ['domains'] as const,
  list: (p: DomainListParams) => ['domains', 'list', p] as const,
  detail: (id: string) => ['domains', 'detail', id] as const,
  dns: (id: string) => ['domains', 'dns', id] as const,
  zones: ['dns', 'zones'] as const,
  zone: (id: string) => ['dns', 'zone', id] as const,
  health: (id: string) => ['dns', 'health', id] as const,
  sync: (id: string) => ['dns', 'sync', id] as const,
  webmail: (id: string) => ['dns', 'webmail', id] as const,
  onboarding: (id: string) => ['domains', 'onboarding', id] as const,
};
export const useDomainsList = (p: DomainListParams) =>
  useQuery({ queryKey: keys.list(p), queryFn: () => domainsApi.list(p), placeholderData: (x) => x });

export const useDomain = (id: string) =>
  useQuery({ queryKey: keys.detail(id), queryFn: () => domainsApi.get(id), enabled: !!id });

/** Onboarding checklist from live state; refreshed every minute while the page is open. */
export const useDomainOnboarding = (id: string) =>
  useQuery({ queryKey: keys.onboarding(id), queryFn: () => domainsApi.onboarding(id), enabled: !!id, refetchInterval: 60_000 });

export const useDomainDns = (id: string) =>
  useQuery({ queryKey: keys.dns(id), queryFn: () => domainsApi.dnsRecords(id), enabled: !!id });

export function useCreateDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: DomainFormValues) => domainsApi.create(b),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.zones });
      notify.success(created.dnsMode === 'managed'
        ? 'Domain added with Managed DNS — point your registrar to the Nifty nameservers'
        : 'Domain added — publish the DNS records to verify');
      for (const n of created.notices ?? []) notify.warning(n, { duration: 8000 });
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not add domain')),
  });
}
export function useUpdateDomain(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Partial<{ status: string; catchAllEnabled: boolean; catchAllTarget: string; dnsMode: 'managed' | 'external' }>) => domainsApi.update(id, b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); qc.invalidateQueries({ queryKey: keys.detail(id) }); qc.invalidateQueries({ queryKey: keys.onboarding(id) }); notify.success('Domain updated'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not update domain')),
  });
}
export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => domainsApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); notify.success('Domain deleted'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not delete domain')),
  });
}
export function useVerifyDomain(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => domainsApi.verify(id),
    onSuccess: async (result) => {
      // Do not announce completion until the detail page is showing the same
      // record statuses returned by the completed server-side DNS check.
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.detail(id) }),
        qc.invalidateQueries({ queryKey: keys.dns(id) }),
      ]);
      const summary = summarizeDnsVerification(result);
      notify[summary.tone](summary.message);
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Verification failed')),
  });
}

/* ---- managed-DNS zone editing (4.12) ---- */

export const useZoneRecords = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.zone(id), queryFn: () => domainsApi.zoneRecords(id), enabled: !!id && enabled });

export const useDnsZones = () =>
  useQuery({ queryKey: keys.zones, queryFn: domainsApi.zones });

export const useZoneHealth = (zoneId: string) =>
  useQuery({ queryKey: keys.health(zoneId), queryFn: () => domainsApi.zoneHealth(zoneId), enabled: !!zoneId, retry: false });

export function useCreateDnsZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domainId: string) => domainsApi.createZone(domainId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.zones }); notify.success('Managed DNS zone created'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not create the DNS zone')),
  });
}

/** Read-only sync preview; fetched only while the review dialog is open. */
export const useZoneSyncPreview = (zoneId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.sync(zoneId), queryFn: () => domainsApi.previewZoneSync(zoneId), enabled: !!zoneId && enabled, retry: false, staleTime: 0, gcTime: 0 });

export function useApplyZoneSync(zoneId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => domainsApi.applyZoneSync(zoneId),
    onSuccess: (report) => {
      qc.invalidateQueries({ queryKey: keys.zone(zoneId) });
      qc.invalidateQueries({ queryKey: keys.sync(zoneId) });
      qc.invalidateQueries({ queryKey: keys.webmail(zoneId) });
      notify.success(report.applied
        ? `${report.applied} record${report.applied === 1 ? '' : 's'} added as drafts — review, then publish`
        : 'The zone already has every record; nothing was added');
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not synchronise DNS records')),
  });
}

export const useWebmailStatus = (zoneId: string) =>
  useQuery({ queryKey: keys.webmail(zoneId), queryFn: () => domainsApi.webmailStatus(zoneId), enabled: !!zoneId, retry: false });

export function useValidateDnsZone(zoneId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => domainsApi.validateZone(zoneId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: keys.zones });
      if (result.ok) {
        notify.success(`Zone validation passed (${result.recordCount} records checked)`);
        return;
      }
      notify.error(result.issues[0]?.message ?? 'Zone validation found errors');
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Zone validation failed')),
  });
}

export function usePublishDnsZone(zoneId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => domainsApi.publishZone(zoneId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.zones });
      qc.invalidateQueries({ queryKey: keys.zone(zoneId) });
      qc.invalidateQueries({ queryKey: keys.health(zoneId) });
      notify.success('DNS changes published');
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not publish DNS changes')),
  });
}

export function useCreateZoneRecord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ZoneRecordPayload) => domainsApi.createRecord(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.zone(id) }); notify.success('DNS record added'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not add the record')),
  });
}

export function useUpdateZoneRecord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, body }: { recordId: string; body: ZoneRecordPayload }) => domainsApi.updateRecord(id, recordId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.zone(id) }); notify.success('DNS record updated'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not update the record')),
  });
}

export function useDeleteZoneRecord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recordId: string) => domainsApi.deleteRecord(id, recordId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.zone(id) }); notify.success('DNS record deleted'); },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not delete the record')),
  });
}

export function useRotateDkim(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => domainsApi.rotateDkim(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: keys.zone(id) });
      qc.invalidateQueries({ queryKey: keys.dns(id) });
      notify.success(`DKIM rotated to ${r.selector} — old key valid until ${new Date(r.overlapUntil).toLocaleDateString()}`);
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'DKIM rotation failed')),
  });
}

export function useSetDomainMailState(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'suspend' | 'resume') => domainsApi.setMailState(id, action),
    onSuccess: (_d, action) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      notify.success(action === 'suspend' ? 'Mail suspended for this domain' : 'Mail resumed for this domain');
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Could not change mail state')),
  });
}
